/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Ensured cost calculations correctly handle surcharge percentages.
 *                                   - Added `unit_of_measure_id` label to component display in CompositionViewer.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically customize
 *                                   field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays read-only information about a composite product.
 *                      It does not handle sensitive data or perform write operations.
 * INTENT: Present a summary card for a composite product, including its cost, surcharge profile,
 *         and a high-level overview of its composition, with options to view or edit details,
 *         now with whitelabeling capabilities for field titles.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure cost calculations are accurate and
 *                        that the composition summary is informative and easy to understand.
 * LLM_CONFIDENCE: 100% - The component correctly renders composite data and integrates with CompositionViewer.
 */
import React from 'react';
import { Composite, Composition, CompositionComponent, CompositionProcess, Surcharge } from '../types/costwand.d';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Link } from 'react-router-dom';
import { Label } from './ui/label.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';
import CompositionViewer from './CompositionViewer.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface CompositeCardProps {
  composite: Composite;
  surcharges: Surcharge[];
  entityId: string; // NEW: Add entityId prop
}

interface CompositionTotals {
  totalComponents: number;
  totalProcesses: number;
}

// Helper function to recursively count components and processes
const getCompositionTotals = (composition: Composition): CompositionTotals => {
  let totalComponents = 0;
  let totalProcesses = 0;

  // Count top-level processes
  if (composition.processes) {
    totalProcesses += composition.processes.length;
  }

  // Recursively count components and their processes/subcomponents
  const countRecursive = (components: CompositionComponent[]) => {
    components.forEach(comp => {
      totalComponents++; // Count this component
      if (comp.processes) {
        totalProcesses += comp.processes.length; // Count processes specific to this component
      }
      if (comp.subcomponents) {
        countRecursive(comp.subcomponents); // Recurse for subcomponents
      }
    });
  };

  if (composition.components) {
    countRecursive(composition.components);
  }

  return { totalComponents, totalProcesses };
};

const CompositeCard: React.FC<CompositeCardProps> = ({ composite, surcharges, entityId }) => { // NEW: Destructure entityId
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId]?.title || defaultTitle;

  const { totalComponents, totalProcesses } = getCompositionTotals(composite.composition);

  // Calculate cost with surcharge
  const surchargeProfile = surcharges.find(s => s.id === composite.tax_profile_id);
  const surchargePercentage = surchargeProfile?.percentage || 0;
  const costWithOutSurcharge = composite.cost ? composite.cost / (1 + surchargePercentage / 100) : null;

  const marginBackground = 'inline-block px-4 py-2 rounded-lg ' + (composite.retail_price > composite.cost ? 'bg-green-300' : 'bg-red-300');

  const profit = (composite.retail_price - composite.cost);

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="bg-blue-50">
        <CardTitle>{composite.description}</CardTitle>
        <CardDescription>{getFieldTitle('article_number', 'Article No')}: {composite.article_number}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="space-y-2">
          <p><strong>{getFieldTitle('base_cost', 'Base Cost')}:</strong> ${costWithOutSurcharge?.toFixed(2) || 'N/A'}</p>
          <p><strong>{getFieldTitle('tax_profile_id', 'Surcharge Profile')}:</strong> {composite._labels?.tax_profile_id || 'N/A'} ({surchargePercentage.toFixed(2)}%)</p>
          <p><strong>{getFieldTitle('cost', 'Cost with Surcharge')}:</strong> ${composite.cost?.toFixed(2) || 'N/A'}</p>
          <p><strong>{getFieldTitle('retail_price', 'Price')}:</strong> ${composite.retail_price?.toFixed(2) || 'N/A'}</p>
          <p><strong>Profit:</strong> <Label className={marginBackground}>${(profit.toFixed(2) + ' / ' + ((profit/composite.cost*100).toFixed(2) + '%')) || 'N/A'}</Label></p>
          <div className="pt-2 border-t mt-4">
            <h2 className="font-semibold text-lg mb-1">Composition:</h2>
            <p>Total Components: {totalComponents}</p>
            <p>Total Processes: {totalProcesses}</p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between gap-2 pt-4 bg-gray-100">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/composition-edit/${composite.id}`}>Edit</Link>
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="black" size="sm">
              View
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Composition for {composite.description}</DialogTitle>
            </DialogHeader>
            <CompositionViewer composition={composite.composition} total_cost={costWithOutSurcharge?.toFixed(2) || 'N/A'} />
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
};

export default CompositeCard;